import { createSelector } from '@reduxjs/toolkit';
import { getFuelIcon } from '@rio-cloud/rio-uikit/fuelTypeUtils';
import type { TreeItem, TreeItemName } from '@rio-cloud/rio-uikit/Tree';
import compact from 'lodash/fp/compact';
import get from 'lodash/fp/get';
import isEmpty from 'lodash/fp/isEmpty';
import isNil from 'lodash/fp/isNil';
import isObject from 'lodash/fp/isObject';

import type { RootState } from '../../configuration/setup/store';
import { UNGROUPED_ITEMS_GROUP_ID } from '../../services/fetchStates';
import type { Asset, CombinedAssetData, Driver, GeoBookingState, Poi, TransformedData } from '../../services/types';
import {
    getChargingStation,
    getCustomerPois,
    getData,
    getDrivers,
    getGroups,
    getSelectedAssetGroupIds,
    getSelectedAssetIds,
    getWorkshopPois,
} from '../app/selectors';
import { getFilteredMapAssetData, getMapGeofences, hasActiveMapFilter, type MapGeofence } from '../map/mapSelectors';
import { getActivityFilter } from '../table/tableSelectors';
import type { ChargingStation } from '../widgets/remoteActionTypes';
import {
    ASSET_DISPLAY_MAP,
    type AssetDisplayNameType,
    type TREE_CATEGORY,
    type TREE_POI_CATEGORY,
    type TREE_ROUTE_PLANNING_CATEGORY,
} from './treeSlice';

export type TreeDriver = {
    id: string;
    name: {
        firstName: string;
        lastName: string;
    };
    type: string;
    groupIds: string[];
};

export type TreeAsset = TreeItem & {
    vin: string;
    licensePlate?: string;
    hasPosition: boolean;
    geoBookingState: GeoBookingState;
    smartRoutePlanningBooked: boolean;
    pairIconName?: string | null;
};

export const getExpandedAssetGroups = (state: RootState): string[] => state.app.tree.expandedAssetGroups;
export const getExpandedDriverGroups = (state: RootState): string[] => state.app.tree.expandedDriverGroups;
export const getTreeCategory = (state: RootState): TREE_CATEGORY => state.app.tree.treeCategory;
export const getTreePoiCategory = (state: RootState): TREE_POI_CATEGORY => state.app.tree.treePoiCategory;
export const getTreeRoutePlanningCategory = (state: RootState): TREE_ROUTE_PLANNING_CATEGORY =>
    state.app.tree.treeRoutePlanningCategory;
export const isTreeOpen = (state: RootState): boolean => state.app.tree.isTreeOpen;
export const showEmptyGroups = (state: RootState): boolean => state.app.tree.showEmptyGroups;
export const showFuelType = (state: RootState): boolean => state.app.tree.showFuelType;
export const showAssetGroups = (state: RootState): boolean => state.app.tree.showAssetGroups;
export const showDriverGroups = (state: RootState): boolean => state.app.tree.showDriverGroups;
export const assetDisplayNameType = (state: RootState): AssetDisplayNameType => state.app.tree.assetDisplayNameType;

export const hasActiveAssetFilter = createSelector(
    getSelectedAssetGroupIds,
    getSelectedAssetIds,
    getActivityFilter,
    (selectedGroups, selectedAssets, activityFilter) =>
        !isEmpty(selectedGroups) || !isEmpty(selectedAssets) || !isEmpty(activityFilter)
);

export const getGlobalSearchValue = (state: RootState): string => state.app.tree.globalSearchValue;

const getDriverFullName = (driver: TreeDriver) => `${driver.name.firstName} ${driver.name.lastName}`;
const getGroupIds = (groupIds: string[] | undefined): string[] =>
    groupIds === undefined || isEmpty(groupIds) ? [UNGROUPED_ITEMS_GROUP_ID] : groupIds;

export const getAssetDisplayName = (asset: Asset | TreeAsset, assetDisplayNameType: AssetDisplayNameType) => {
    if (assetDisplayNameType === ASSET_DISPLAY_MAP.vin) {
        return !isEmpty(asset.vin) ? asset.vin : asset.name;
    }
    if (assetDisplayNameType === ASSET_DISPLAY_MAP.licensePlate) {
        return !isEmpty(asset.licensePlate) ? asset.licensePlate : asset.name;
    }
    return asset.name;
};

export const getAssetDisplayNameSubtitles = (asset: Asset | TreeAsset, assetDisplayNameType: AssetDisplayNameType) => {
    const takeNameIfVIN = asset.vin && asset.name;
    const takeNameIfLicensePlate = asset.licensePlate && asset.name;

    return compact([
        assetDisplayNameType === ASSET_DISPLAY_MAP.vin ? takeNameIfVIN : asset.vin,
        assetDisplayNameType === ASSET_DISPLAY_MAP.licensePlate ? takeNameIfLicensePlate : asset.licensePlate,
    ]);
};

const transformAssetForTree = (rawAsset: CombinedAssetData, showFuelType: boolean): TreeAsset => ({
    id: rawAsset.vehicleId,
    name: rawAsset.name,
    type: rawAsset.type,
    subType: showFuelType ? rawAsset.fuelType : undefined,
    vin: rawAsset.vin,
    licensePlate: rawAsset.licensePlate,
    iconName: rawAsset.type,
    pairIconName: showFuelType && rawAsset.fuelType ? getFuelIcon(rawAsset.fuelType).replace(/^icon-/, '') : null,
    groupIds: getGroupIds(rawAsset.groupIds),
    hasPosition: !isNil(rawAsset.longitude) && !isNil(rawAsset.latitude),
    geoBookingState: rawAsset.geoBookingState,
    smartRoutePlanningBooked: rawAsset.smartRoutePlanningBooked,
});

export const transformDriverForTree = (driver: Driver): TreeDriver => ({
    id: driver.driverId,
    name: {
        firstName: driver.firstName,
        lastName: driver.lastName,
    },
    type: 'driver',
    groupIds: getGroupIds(driver.groupIds),
});

export const getTreeAssets = createSelector([getData, showFuelType], (rawData, showFuelType) =>
    rawData?.map(asset => transformAssetForTree(asset, showFuelType))
);

export const getTreeDrivers = createSelector(getDrivers, getData, (drivers, rawData) => {
    if (!drivers || !rawData) {
        return [];
    }

    const driversOnAssets = rawData.filter(get('driverId')).map(get('driverId'));

    const filteredDrivers = drivers.map(driver => {
        if (!driversOnAssets.includes(driver.driverId)) {
            return false;
        }
        return transformDriverForTree(driver);
    });
    return compact(filteredDrivers);
});

export const getTreeGroups = createSelector(getGroups, groups =>
    groups?.map(group => ({
        id: group.id,
        name: group.name,
        position: group.position,
    }))
);

export const findAssetsByNameVinAndLicensePlate = (assets: TreeAsset[] | TransformedData[], searchValue: string) => {
    const lowerCaseSearchValue = searchValue.toLowerCase();
    const assetName = (asset: TreeAsset | TransformedData) =>
        isObject(asset.name)
            ? `${(asset.name as TreeItemName).firstName} ${(asset.name as TreeItemName).lastName}`
            : asset.name;
    return assets.filter(
        asset =>
            assetName(asset).toLowerCase().includes(lowerCaseSearchValue) ||
            asset.vin.toLowerCase().includes(lowerCaseSearchValue) ||
            asset.licensePlate?.toLowerCase().includes(lowerCaseSearchValue)
    );
};

export const getFilteredAssetsByName = createSelector(
    getFilteredMapAssetData,
    getGlobalSearchValue,
    (assets, globalSearchValue) =>
        assets && !isEmpty(globalSearchValue)
            ? (findAssetsByNameVinAndLicensePlate(assets, globalSearchValue) as TransformedData[])
            : []
);

const filterPoiByNameAndAddress = (item: Poi, searchValue: string) => {
    const { name, address } = item;
    return (
        (name || '').toLowerCase().includes(searchValue.toLowerCase()) ||
        (address || '').toLowerCase().includes(searchValue.toLowerCase())
    );
};

const filterGeofenceByName = (item: MapGeofence, searchValue: string) => {
    const { name } = item;
    return (name || '').toLowerCase().includes(searchValue.toLowerCase());
};

const filterChargingStationsByName = (item: ChargingStation, searchValue: string) => {
    const { name } = item.markerProps;
    return (name || '').toLowerCase().includes(searchValue.toLowerCase());
};

const filterPois = (pois: Poi[], searchValue: string) =>
    pois && !isEmpty(searchValue) ? pois.filter(poi => filterPoiByNameAndAddress(poi, searchValue)) : [];

export const getFilteredCustomerPois = createSelector(
    getCustomerPois,
    getGlobalSearchValue,
    (customerPois, globalSearchValue) => filterPois(customerPois, globalSearchValue)
);

export const getFilteredWorkshopPois = createSelector(
    getWorkshopPois,
    getGlobalSearchValue,
    (workshopPois, globalSearchValue) => filterPois(workshopPois, globalSearchValue)
);

const filterGeofences = (geofences: MapGeofence[], searchValue: string) =>
    geofences && !isEmpty(searchValue) ? geofences.filter(geofence => filterGeofenceByName(geofence, searchValue)) : [];

export const getFilteredGeofences = createSelector(
    getMapGeofences,
    getGlobalSearchValue,
    (geofences, globalSearchValue) => filterGeofences(geofences, globalSearchValue)
);

const filterChargingStations = (chargingStations: ChargingStation[], searchValue: string) =>
    chargingStations && !isEmpty(searchValue)
        ? chargingStations.filter(chargingStation => filterChargingStationsByName(chargingStation, searchValue))
        : [];

export const getFilteredChargingStations = createSelector(
    getChargingStation,
    getGlobalSearchValue,
    (chargingStations, globalSearchValue) => filterChargingStations(chargingStations, globalSearchValue)
);

const filterByName = (driver: TreeDriver, searchValue = '') =>
    getDriverFullName(driver).toLowerCase().includes(searchValue.toLowerCase());

const filterDriversNameByValue = (drivers: TreeDriver[] = [], searchValue = '') =>
    drivers.filter(driver => filterByName(driver, searchValue));

export const getFilteredDriversByName = createSelector(
    getTreeDrivers,
    getGlobalSearchValue,
    getFilteredMapAssetData,
    hasActiveMapFilter,
    (drivers, globalSearchValue, filteredMapAssets, hasActiveFilter) => {
        if (isEmpty(globalSearchValue) || !drivers) {
            return [];
        }

        // When there are no active filter, means assets, groups or other tree filter,
        // then take the entire list of drivers into account
        if (!hasActiveFilter) {
            return filterDriversNameByValue(drivers, globalSearchValue);
        }

        // In case there are active map filter, limit the driver search to the list of the assets
        const filteredDriverIds = filteredMapAssets.map(asset => asset.driverId);

        return drivers.filter(
            driver => filteredDriverIds.includes(driver.id) && filterByName(driver, globalSearchValue)
        );
    }
);
